import { useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

const FeedbackPanel = ({ feedback }) => {
  const [activeTab, setActiveTab] = useState("overview");

  if (!feedback) {
    return null;
  }

  // Convert string score to number (e.g., "8/10" -> 8)
  const convertScoreToNumber = (scoreStr) => {
    if (!scoreStr) return NaN;

    // Try to extract the number before slash
    const match = scoreStr.match(/(\d+(?:\.\d+)?)\s*\/\s*\d+/);
    if (match) {
      return parseFloat(match[1]);
    }

    // If no slash format, try to parse as is
    const numericScore = parseFloat(scoreStr);
    return isNaN(numericScore) ? NaN : numericScore;
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    const numScore = convertScoreToNumber(score);
    if (isNaN(numScore)) return "text-gray-500";

    if (numScore >= 8) return "text-green-600";
    if (numScore >= 6) return "text-blue-600";
    if (numScore >= 4) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    // Removed explicit bg-white, shadow, assuming parent (InterviewPage) provides the card container.
    // Added base text color for the panel.
    <div className="text-slate-200 transition-all duration-300">
      <div className="p-4 sm:p-6 pb-0 border-b border-slate-700">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-2">
          <h3 className="text-2xl font-bold text-white flex items-center mb-3 sm:mb-0">
            <svg
              className="w-6 h-6 mr-2.5 text-blue-400" // Adjusted icon color and size
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            AI Feedback
          </h3>

          <div className="flex items-center space-x-2 bg-slate-700/50 rounded-lg p-1">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-3.5 py-1.5 text-sm font-semibold rounded-md transition-all duration-200 ${
                activeTab === "overview"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-slate-300 hover:bg-slate-600/70 hover:text-white"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("details")}
              className={`px-3.5 py-1.5 text-sm font-semibold rounded-md transition-all duration-200 ${
                activeTab === "details"
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-slate-300 hover:bg-slate-600/70 hover:text-white"
              }`}
            >
              Details
            </button>
          </div>
        </div>
      </div>

      {/* Score cards at the top */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 sm:p-6 bg-slate-800/40 border-y border-slate-700">
        {/* Clarity Score */}
        <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600 shadow-lg">
          <div className="text-xs text-slate-400 mb-1 uppercase tracking-wider">Clarity</div>
          <div
            className={`text-2xl font-bold ${getScoreColor(
              feedback.ClarityScore
            )}`}
          >
            {feedback.ClarityScore || "N/A"}
          </div>
        </div>
        {/* Relevance Score */}
        <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600 shadow-lg">
          <div className="text-xs text-slate-400 mb-1 uppercase tracking-wider">Relevance</div>
          <div
            className={`text-2xl font-bold ${getScoreColor(
              feedback.RelevanceScore
            )}`}
          >
            {feedback.RelevanceScore || "N/A"}
          </div>
        </div>
        {/* Technical Score */}
        <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600 shadow-lg">
          <div className="text-xs text-slate-400 mb-1 uppercase tracking-wider">Technical</div>
          <div
            className={`text-2xl font-bold ${getScoreColor(
              feedback.TechnicalCorrectnessScore
            )}`}
          >
            {typeof feedback.TechnicalCorrectnessScore === "undefined" ||
            feedback.TechnicalCorrectnessScore === "N/A"
              ? "N/A"
              : feedback.TechnicalCorrectnessScore}
          </div>
        </div>
      </div>

      {/* Content based on active tab */}
      <div className="p-4 sm:p-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            {feedback.OverallAssessment && (
              <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-700/50">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-blue-400 mr-3 mt-1 flex-shrink-0" // Adjusted icon color and margin
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-blue-200 text-sm">{feedback.OverallAssessment}</p>
                </div>
              </div>
            )}

            {feedback.Strengths && feedback.Strengths.length > 0 && (
              <div>
                <h4 className="text-base font-semibold text-slate-100 mb-2.5 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-green-400" // Adjusted icon color
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Key Strengths
                </h4>
                <div className="space-y-2 pl-7">
                  {feedback.Strengths.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <svg
                        className="w-4 h-4 text-green-400 mr-2.5 mt-0.5 flex-shrink-0" // Adjusted icon color and margin
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-slate-300">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {feedback.AreasForImprovement &&
              feedback.AreasForImprovement.length > 0 && (
                <div>
                  <h4 className="text-base font-semibold text-slate-100 mb-2.5 flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-yellow-400" // Adjusted icon color
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Areas for Improvement
                  </h4>
                <div className="space-y-2 pl-7">
                    {feedback.AreasForImprovement.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <svg
                        className="w-4 h-4 text-yellow-400 mr-2.5 mt-0.5 flex-shrink-0" // Adjusted icon color and margin
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      <p className="text-sm text-slate-300">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {feedback.ExampleAnswer && (
              <div>
                <h4 className="text-base font-semibold text-slate-100 mb-2.5 flex items-center">
                  <svg
                    className="w-5 h-5 mr-2 text-blue-400" // Adjusted icon color
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Answer Suggestion
                </h4>
                <div className="prose prose-sm sm:prose dark:prose-invert max-w-none p-3 bg-slate-700/40 rounded-lg border border-slate-600 text-slate-200">
                  {/* Ensure prose styles provide good contrast for links, code blocks etc. */}
                  {/* Tailwind's dark:prose-invert helps a lot here. */}
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {feedback.ExampleAnswer}
                  </Markdown>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "details" && (
          <div className="space-y-5"> {/* Increased spacing for details tab */}
            <div className="border-t border-b border-slate-700 py-5"> {/* Adjusted padding and border */}
              <h4 className="text-sm font-medium text-slate-300 mb-2"> {/* Adjusted text color */}
                Clarity Score: {feedback.ClarityScore || "N/A"}
              </h4>
              <div className="flex items-center mb-2">
                <div className="w-full bg-slate-600 rounded-full h-2.5"> {/* Darker background for progress bar track */}
                  <div
                    className={`h-2.5 rounded-full ${getScoreColor( // Ensure getScoreColor provides sufficient contrast for bg
                      feedback.ClarityScore
                    ).replace("text-", "bg-")}`} // Assuming getScoreColor class names work for bg
                    style={{
                      width: `${Math.min(
                        convertScoreToNumber(feedback.ClarityScore) * 10,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
              <p className="text-xs text-slate-400 italic"> {/* Adjusted text color */}
                How clear and well-structured the response is.
              </p>
            </div>

            <div className="border-b border-slate-700 py-5"> {/* Adjusted padding and border */}
              <h4 className="text-sm font-medium text-slate-300 mb-2"> {/* Adjusted text color */}
                Relevance Score: {feedback.RelevanceScore || "N/A"}
              </h4>
              <div className="flex items-center mb-2">
                <div className="w-full bg-slate-600 rounded-full h-2.5"> {/* Darker background for progress bar track */}
                  <div
                    className={`h-2.5 rounded-full ${getScoreColor( // Ensure getScoreColor provides sufficient contrast for bg
                      feedback.RelevanceScore
                    ).replace("text-", "bg-")}`} // Assuming getScoreColor class names work for bg
                    style={{
                      width: `${Math.min(
                        convertScoreToNumber(feedback.RelevanceScore) * 10,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
              <p className="text-xs text-slate-400 italic"> {/* Adjusted text color */}
                How well the response addresses the question.
              </p>
            </div>

            <div className="border-b border-slate-700 py-5"> {/* Adjusted padding and border */}
              <h4 className="text-sm font-medium text-slate-300 mb-2"> {/* Adjusted text color */}
                Technical Correctness Score:{" "}
                {feedback.TechnicalCorrectnessScore || "N/A"}
              </h4>
              <div className="flex items-center mb-2">
                <div className="w-full bg-slate-600 rounded-full h-2.5"> {/* Darker background for progress bar track */}
                  <div
                    className={`h-2.5 rounded-full ${getScoreColor( // Ensure getScoreColor provides sufficient contrast for bg
                      feedback.TechnicalCorrectnessScore
                    ).replace("text-", "bg-")}`} // Assuming getScoreColor class names work for bg
                    style={{
                      width: `${Math.min(
                        convertScoreToNumber(
                          feedback.TechnicalCorrectnessScore
                        ) * 10,
                        100
                      )}%`,
                    }}
                  ></div>
                </div>
              </div>
              <p className="text-xs text-slate-400 italic"> {/* Adjusted text color */}
                How accurate the technical content of the response is.
              </p>
            </div>

            {feedback.OverallAssessment && (
              <div className="pt-3"> {/* Adjusted padding */}
                <h4 className="text-sm font-medium text-slate-300 mb-2"> {/* Adjusted text color */}
                  Detailed Assessment
                </h4>
                <p className="text-sm text-slate-200 leading-relaxed"> {/* Adjusted text color and leading */}
                  {feedback.OverallAssessment}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPanel;
